import { useRef, useState } from 'react';
import { Menu, ChevronDown } from 'lucide-react';
import logo from '../assets/logo.png';
import { WalletConnectButton } from './WalletConnectButton';
import { Modal } from './Modal';
import { useWallet } from '../hooks/WalletContext';

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  const { publicKey } = useWallet();
  const accountAddress = publicKey?.toString() || null;

  const shortenAddress = (addr: string) =>
    `${addr.slice(0, 6)}...${addr.slice(-4)}`;

  useState(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  const handleMenuClick = (item: string) => {
    setMenuOpen(false);
    if (item === 'Token Presale') {
      window.open('https://ai-scripts.vercel.app', '_blank');
    }
    else if (item === "More Scripts") {
      window.open('https://ai-scripts.vercel.app/scripts', '_blank');
    }
    else {
      setActiveModal(item);
    }
  };

  const closeModal = () => setActiveModal('');

  return (
    <>
      <header className="w-full px-4 sm:px-6 py-4 flex justify-between items-center bg-white border-b border-slate-200 shadow-sm relative z-50">
        <div className="flex items-center gap-4 relative">
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex items-center gap-1 text-slate-700 text-sm font-semibold px-3 py-1 rounded-lg hover:bg-slate-100 border border-slate-200 transition-all"
            >
              <Menu size={18} />
              <span className="hidden sm:inline">Menu</span>
              <ChevronDown size={16} />
            </button>

            {menuOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden text-sm z-50">
                {[
                  'More Scripts',
                  'Refer and Earn',
                  'Token Presale',
                  'Contact Us',
                  'About',
                ].map((item) => (
                  <button
                    key={item}
                    className="w-full text-left px-4 py-2 hover:bg-slate-100 text-slate-800"
                    onClick={() => handleMenuClick(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <img
              src={logo}
              alt="Solana AI Miner Logo"
              className="w-10 h-10 object-contain rounded-xl shadow-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          {accountAddress && (
            <span className="hidden sm:inline-block font-mono text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-xl border border-slate-300">
              {shortenAddress(accountAddress)}
            </span>
          )}
          <WalletConnectButton />
        </div>
      </header>

      <Modal isOpen={!!activeModal} onClose={closeModal} title={activeModal}>
        {activeModal === 'Refer and Earn' && accountAddress ? (
          <div className="space-y-4">
            <p className="text-slate-700 text-sm leading-relaxed">
              Invite your friends to start mining with AI SolMiner. You’ll earn{' '}
              <span className="font-semibold text-indigo-600">3%</span> of the
              profits your referrals generate — passively and automatically.
            </p>
            <div className="bg-slate-100 border border-slate-300 rounded-lg px-4 py-3 flex items-center justify-between">
              <span className="text-slate-800 text-sm font-mono break-all">
                https://ai-solminer.netlify.app/ref-{accountAddress.slice(-6)}
              </span>
              <button
                onClick={() =>
                  navigator.clipboard.writeText(
                    `https://ai-solminer.netlify.app/ref-${accountAddress.slice(-6)}`
                  )
                }
                className="ml-4 px-3 py-1 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition"
              >
                Copy
              </button>
            </div>
          </div>
        ) : activeModal === 'Contact Us' ? (
          <div className="space-y-4 text-slate-700">
            <p className="text-sm leading-relaxed">
              Encountered a bug? Need help with your account? Have suggestions to improve our products?
              We're here to help.
            </p>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-sm text-slate-700 mb-2">
                For any bugs, problems, or support inquiries, feel free to reach out to our team:
              </p>
              <a
                href="mailto:ai-cryptotrading@engineer.com"
                className="inline-block px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition"
              >
                Email Us: ai-cryptotrading@engineer.com
              </a>
            </div>
          </div>
        ) : activeModal === 'About' ? (
          <div className="space-y-4 text-slate-700">
            <h2 className="text-xl font-bold text-black-600">AI SOLMINER</h2>
            <p className="text-sm">Version: <span className="font-semibold text-slate-800">v1.0.3</span></p>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <h3 className="text-md font-semibold text-indigo-500 mb-2">Changelog</h3>
              <ul className="text-sm list-disc list-inside space-y-1">
                <li>Integrated AI-enhanced mining strategy engine</li>
                <li>Real-time activity feed</li>
                <li>Phantom wallet connection & referral system added</li>
                <li>General UI/UX polish and bug fixes</li>
              </ul>
            </div>
          </div>
        ) : (
          <p className="text-slate-600 text-sm">
            Please connect your wallet to access your referral link. {activeModal}
          </p>
        )}
      </Modal>
    </>
  );
};
